import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiDownload } from 'react-icons/bi';
import { downloadExcelExport } from '@/api/export.api';
import { ApiError } from '@/api/errors';
import { Banner } from '@/components/common/Banner';
import './ExportButton.css';

/** Everything a `DataTable` (or a standalone report page) needs to wire its "Export to Excel"
 * button to a real T74 export endpoint — the endpoint's own filter/search/sort query params
 * (never `page`/`pageSize`, which every export endpoint rejects) plus a sensible fallback
 * filename for the rare case the `Content-Disposition` header is missing. */
export interface ExportButtonConfig {
  /** e.g. `/students/export` — same base path as the list endpoint, `/export` suffixed. */
  endpoint: string;
  /** The page's *current* filters/search/sort — same shape as its list call, minus page/pageSize. */
  params?: Record<string, unknown>;
  fallbackFilename: string;
}

interface ExportButtonProps {
  /** Omit to keep the pre-T75 disabled "coming soon" stub (no wired endpoint for this table). */
  config?: ExportButtonConfig;
}

/**
 * T75: GETs `config.endpoint` with the caller's current filters and streams the resulting
 * `.xlsx` to disk. Shows a busy label while the request is in flight and a `Banner` with a
 * graceful message on failure — including the narrower per-module export RBAC (a role that can
 * view the list may still get a 403 trying to export it, 07-cross-cutting.md §5 `FORBIDDEN`).
 */
export function ExportButton({ config }: ExportButtonProps) {
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!config) {
    const tooltip = t('table.exportComingSoon');
    return (
      <button type="button" className="fa-btn fa-btn--secondary" disabled title={tooltip}>
        <BiDownload aria-hidden="true" />
        <span>{t('table.export')}</span>
        <span className="fa-visually-hidden">{tooltip}</span>
      </button>
    );
  }

  const { endpoint, params, fallbackFilename } = config;

  async function handleClick() {
    setExporting(true);
    setError(null);
    try {
      await downloadExcelExport(endpoint, params, fallbackFilename);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      setError(apiError.status === 403 ? t('table.exportForbidden') : t('table.exportError'));
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="fa-export-button">
      <button
        type="button"
        className="fa-btn fa-btn--secondary"
        onClick={handleClick}
        disabled={exporting}
        aria-busy={exporting}
      >
        {exporting ? (
          <span className="fa-export-button__spinner" aria-hidden="true" />
        ) : (
          <BiDownload aria-hidden="true" />
        )}
        <span>{exporting ? t('table.exporting') : t('table.export')}</span>
      </button>
      {error && (
        <Banner tone="danger" assertive>
          {error}
        </Banner>
      )}
    </div>
  );
}
